<script lang="ts">
export default {
  name: 'KkFilterItem',
}
</script>

<script lang="ts" setup>
import { ref, computed, onMounted } from 'vue'

export type FilterItem = {
  name: string
  [key: string]: string | number
}

export interface Props {
  list: FilterItem[]
  formValues: Record<string, unknown>
  headText: string
  valueKey?: string
  storeName?: string
  filterName?: string
  filterType: string
  filterClass?: string
}

export type SetFilterValue = {
  target: string
  value: string | number
}

export type ToggleFilterValue = {
  target: string
  allChecked: boolean
  initialValues: (string | number)[]
}

export interface Emits {
  (key: 'set-filter-values', value: SetFilterValue): void
  (key: 'toggle-filter-values', value: ToggleFilterValue): void
  (key: 'change-check-box'): void
}

const props = withDefaults(defineProps<Props>(), {
  valueKey: 'id',
  storeName: 'companiesSearchAttributes',
  filterName: '',
  fieldClass: 'p-filters__item__l__12 p-filters__item__s__12 p-filters__item__m__12',
})
const emits = defineEmits<Emits>()
const allChecked = ref(true)

const filterTypeFormValues = computed(() => {
  return props.formValues[props.filterType]
})

const fieldIdPrefix = computed(() => {
  return `${props.storeName}_${props.filterType}_form_${props.filterType}`
})

const setFilterValues = (payload: SetFilterValue) => {
  allChecked.value = false
  emits('set-filter-values', payload)
  handleChangeCheckBox()
}

const toggleFilterValues = (payload: ToggleFilterValue) => {
  emits('toggle-filter-values', payload)
  handleChangeCheckBox()
}

const toggleAllChecked = () => {
  toggleFilterValues({
    target: props.filterType,
    allChecked: allChecked.value,
    initialValues: props.list.map(item => item[props.valueKey])
  })
}

const handleChangeCheckBox = () => {
  emits('change-check-box')
}

onMounted(() => {
  if ((filterTypeFormValues.value as unknown[]).length !== props.list.length) {
    allChecked.value = false
  }
})
</script>

<template>
  <div v-if="props.list" :class="props.fieldClass" class="p-modalLikeBox">
    <div class="p-modalLikeBox__header">
      <p class="p-modalLikeBox__header__text">
        {{ props.filterName }}
        <input
          :id="`${fieldIdPrefix}_all`"
          v-model="allChecked"
          class="c-checkbox__input"
          type="checkbox"
          @change="toggleAllChecked"
        >
        <label class="c-checkbox__label" :for="`${fieldIdPrefix}_all`">{{ props.headText }}</label>
      </p>
    </div>
    <div class="p-modalLikeBox__body">
      <ul class="c-checkbox">
        <li
          v-for="( item, i ) in props.list"
          :key="i"
          class="c-checkbox__item"
        >
          <input
            :id="`${fieldIdPrefix}_${item[valueKey]}`"
            class="c-checkbox__input"
            type="checkbox"
            :checked="(filterTypeFormValues as unknown[]).includes(item[props.valueKey])"
            :value="item.uuid"
            @change="setFilterValues({ target: props.filterType, value: item[props.valueKey] })"
          >
          <label
            class="c-checkbox__label"
            :for="`${fieldIdPrefix}_${item[props.valueKey]}`"
            v-text="item.name"
          />
        </li>
      </ul>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.p-modalLikeBox {
  &__header__text {
    background: #F5F6F9;
    color: #3A3B3F;
    .c-checkbox__label {
      margin-left: 16px;
      font-weight: normal;
    }
  }
}
.p-modalLikeBox:not(:first-of-type) {
  margin-top: 12px;
}
.p-modalLikeBox:not(:last-of-type) {
  margin-bottom: 12px;
}
.c-checkbox {
  padding: 24px;
}
</style>
